import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CakeIcon from '@mui/icons-material/Cake';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HomeIcon from '@mui/icons-material/Home';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArchiveIcon from '@mui/icons-material/Archive';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const drawerWidth = 280;

// Sortable List Item Component
const SortableListItem = ({ list, selectedListId, onSelectList, onMenuOpen, getListIcon, mobileOpen, onDrawerToggle, reorderMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: list.id, disabled: !reorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      secondaryAction={
        <IconButton
          edge="end"
          onClick={(e) => onMenuOpen(e, list)}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton
        selected={selectedListId === list.id}
        onClick={() => {
          onSelectList(list.id);
          if (mobileOpen) {
            onDrawerToggle();
          }
        }}
      >
        {reorderMode && (
          <ListItemIcon
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              touchAction: 'none',
              minWidth: 36,
              '&:active': {
                cursor: 'grabbing'
              }
            }}
          >
            <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          </ListItemIcon>
        )}
        <ListItemIcon>{getListIcon(list.type)}</ListItemIcon>
        <ListItemText primary={list.name} />
      </ListItemButton>
    </ListItem>
  );
};

const Sidebar = ({ lists, selectedListId, onSelectList, onAddList, onDeleteList, onUpdateList, mobileOpen, onDrawerToggle }) => {
  const [newListDialogOpen, setNewListDialogOpen] = React.useState(false);
  const [newListName, setNewListName] = React.useState('');
  const [newListType, setNewListType] = React.useState('todo');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedList, setSelectedList] = React.useState(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editListName, setEditListName] = React.useState('');
  const [reorderMode, setReorderMode] = React.useState(false);
  const [archivedOpen, setArchivedOpen] = React.useState(false);

  // Sensors for drag and drop (pointer, touch, keyboard)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddList = async () => {
    if (newListName.trim()) {
      try {
        await onAddList(newListName.trim(), newListType);
        setNewListName('');
        setNewListType('todo');
        setNewListDialogOpen(false);
      } catch (error) {
        console.error('Failed to create list:', error);
        alert('Failed to create list. Please try again.');
      }
    }
  };

  const handleMenuOpen = (event, list) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedList(list);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedList(null);
  };

  const handleEditClick = () => {
    setEditListName(selectedList.name);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    onDeleteList(selectedList.id);
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (editListName.trim() && selectedList) {
      try {
        await onUpdateList(selectedList.id, { name: editListName.trim() });
        setEditDialogOpen(false);
        setEditListName('');
      } catch (error) {
        console.error('Failed to update list:', error);
        alert('Failed to update list name. Please try again.');
      }
    }
  };

  const activeLists = lists.filter(l => !l.archived);
  const archivedLists = lists.filter(l => l.archived);

  const handleArchiveClick = () => {
    onUpdateList(selectedList.id, { archived: true });
    handleMenuClose();
  };

  const handleUnarchiveClick = () => {
    onUpdateList(selectedList.id, { archived: false });
    handleMenuClose();
  };

  // Handle drag end event for reordering lists
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = activeLists.findIndex(list => list.id === active.id);
      const newIndex = activeLists.findIndex(list => list.id === over.id);

      const reorderedLists = arrayMove(activeLists, oldIndex, newIndex);

      // Update order in Firebase for each list
      const updates = reorderedLists.map((list, index) =>
        onUpdateList(list.id, { order: index })
      );
      await Promise.all(updates);
    }
  };

  const getListIcon = (type) => {
    switch (type) {
      case 'todo':
        return <CheckBoxIcon />;
      case 'birthday':
        return <CakeIcon />;
      case 'expense':
        return <AttachMoneyIcon />;
      case 'food':
        return <RestaurantIcon />;
      case 'recipe':
        return <MenuBookIcon />;
      default:
        return <CheckBoxIcon />;
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="primary">
          Notepad
        </Typography>
        <IconButton
          size="small"
          onClick={() => setReorderMode(!reorderMode)}
          color={reorderMode ? 'primary' : 'default'}
        >
          <SwapVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <List sx={{ flexGrow: 1, overflow: 'auto', pt: 1 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            selected={selectedListId === 'home'}
            onClick={() => {
              onSelectList('home');
              if (mobileOpen) {
                onDrawerToggle();
              }
            }}
          >
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            selected={selectedListId === 'past-days'}
            onClick={() => {
              onSelectList('past-days');
              if (mobileOpen) {
                onDrawerToggle();
              }
            }}
          >
            <ListItemIcon><MonitorHeartIcon /></ListItemIcon>
            <ListItemText primary="Wellness Log" />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, mx: 2 }} />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={activeLists.map(list => list.id)}
            strategy={verticalListSortingStrategy}
          >
            {activeLists.map((list) => (
              <SortableListItem
                key={list.id}
                list={list}
                selectedListId={selectedListId}
                onSelectList={onSelectList}
                onMenuOpen={handleMenuOpen}
                getListIcon={getListIcon}
                mobileOpen={mobileOpen}
                onDrawerToggle={onDrawerToggle}
                reorderMode={reorderMode}
              />
            ))}
          </SortableContext>
        </DndContext>

        {archivedLists.length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setArchivedOpen(!archivedOpen)} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ArchiveIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Archived"
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
                {archivedOpen ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={archivedOpen}>
              {archivedLists.map((list) => (
                <ListItem
                  key={list.id}
                  disablePadding
                  secondaryAction={
                    <IconButton edge="end" onClick={(e) => handleMenuOpen(e, list)} size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    selected={selectedListId === list.id}
                    onClick={() => {
                      onSelectList(list.id);
                      if (mobileOpen) onDrawerToggle();
                    }}
                    sx={{ opacity: 0.7 }}
                  >
                    <ListItemIcon>{getListIcon(list.type)}</ListItemIcon>
                    <ListItemText
                      primary={list.name}
                      primaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Collapse>
          </>
        )}
      </List>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewListDialogOpen(true)}
        >
          New List
        </Button>
      </Box>

      {/* Add List Dialog */}
      <Dialog open={newListDialogOpen} onClose={() => setNewListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel>List Type</InputLabel>
            <Select
              value={newListType}
              onChange={(e) => setNewListType(e.target.value)}
              label="List Type"
            >
              <MenuItem value="todo">To-Do List</MenuItem>
              <MenuItem value="birthday">Birthday List</MenuItem>
              <MenuItem value="expense">Expense Tracker</MenuItem>
              <MenuItem value="food">Food List</MenuItem>
              <MenuItem value="recipe">Recipe Book</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewListDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddList} variant="contained" disabled={!newListName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* List Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {!selectedList?.archived && (
          <MenuItem onClick={handleEditClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Name</ListItemText>
          </MenuItem>
        )}
        {selectedList?.archived ? (
          <MenuItem onClick={handleUnarchiveClick}>
            <ListItemIcon>
              <UnarchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unarchive</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleArchiveClick}>
            <ListItemIcon>
              <ArchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit List Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit List Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            value={editListName}
            onChange={(e) => setEditListName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={!editListName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
